import {computed, DestroyRef, inject, Injectable, Signal, signal} from '@angular/core';
import {defer, finalize, MonoTypeOperatorFunction, Observable} from 'rxjs';
import {ISearchStep} from '../../types/ISearchProgress';

export interface ILoadingState {
  message: string;
  steps: ISearchStep[];
}

/**
 * Drives the app-wide blocking loading overlay rendered once in the app root.
 * Several callers can be active at once, the most recently added one's message is shown.
 * Inline or non-blocking loading (spinners, button states) should stay local to the component.
 */
@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private readonly entries = signal<Signal<ILoadingState | null>[]>([]);

  readonly state = computed(() =>
    this.entries().map(entry => entry()).filter(state => state !== null).at(-1) ?? null
  );

  readonly message = computed(() => this.state()?.message ?? null);

  readonly active = computed(() => this.state() !== null);

  /**
   * Shows the overlay until the returned function is called. Prefer track() for a single
   * observable, this is for flows that span several requests.
   */
  show(message: string): () => void {
    return this.add(signal({message, steps: []}));
  }

  /** Shows the overlay from subscribe until the observable completes, errors or is unsubscribed */
  track<T>(message: string): MonoTypeOperatorFunction<T> {
    return (source: Observable<T>) => defer(() => {
      const hide = this.show(message);
      return source.pipe(finalize(hide));
    });
  }

  /**
   * Shows the overlay whenever `when` is true, for loading state a store already tracks.
   * Must be called in an injection context, it stops watching when that context is destroyed.
   */
  watch(message: string, when: () => boolean, steps: () => ISearchStep[] = () => []): void {
    const remove = this.add(computed(() => when() ? {message, steps: steps()} : null));
    inject(DestroyRef).onDestroy(remove);
  }

  private add(entry: Signal<ILoadingState | null>): () => void {
    this.entries.update(entries => [...entries, entry]);
    return () => this.entries.update(entries => entries.filter(e => e !== entry));
  }
}
